import React from "react";
import { useState, useEffect } from "react";
import ModelViewer from "./ModelViewer";
import { SHOW_MODEL_TOOLBAR } from "../config/experiment";
import ToHomeButton from "./buttons/ToHomeButton";
import ColoredPartSelecter from "./buttons/ColoredPartSelecter";
import ShowHintButton from "./buttons/ShowHintButton";
import ExerciseWorkspace from "./helpers/exercise/ExerciseWorkspace";
import PartNextButton from "./helpers/exercise/PartNextButton";
import StepGuide from "./helpers/exercise/StepGuide";
import DirectionNote from "./helpers/exercise/DirectionNote";
import CachedImage from "./helpers/CachedImage";
import { useHintCamera } from "./helpers/exercise/useHintCamera";
import { logPartColorChange } from '../utils/experimentLog';
import { getDrawingUrl, getMotifReference } from "../config/drawings";

// 各問の X-1：誤っている部品を着色して選び、プルダウンの横の【次に進む】で X-2 へ進む。
// 説明シートの列は置かず、モチーフ・誤りの2列を画面の幅いっぱいに取る（2026-10-14）。
// 説明は書かせない（2026-10-14週例。旧＝着色しながら説明シートに自由記述）。部品はプルダウンで選んだ時点で
// 赤く着色され、何度でも選び直せる（選び直すたびに part_color_changes に記録）。
// 部品が選ばれていれば（「△わからない」も可）【次に進む】が押せ、onComplete(selectedPart, cameraPosition, '', detail) で X-2 へ進む。
// initialSelectedPart / locked: 回答済みの問へタブで戻ってきたときに使う。
// 当時の着色を復元して見せ、部品の選び直しと【次に進む】は押せなくする
// （回答は送信済みなので二重送信させない）。
export default function PartSelect({
  number,
  glbPath,
  setSelectedPart: parentSetSelectedPart,
  onComplete,
  initialSelectedPart = "",
  locked = false,
}) {
  const errorGlbPath = glbPath || "model1";
  const motifGlbPath = errorGlbPath.startsWith("cube") ? "cube" : "model";
  const [selectedPart, setLocalSelectedPart] = useState(initialSelectedPart);

  // 親から渡される問題（glbPath）が変わったら選択をリセット
  // （回答済みの問へ戻ったときは当時の選択を初期値として復元する）
  useEffect(() => {
    setLocalSelectedPart(initialSelectedPart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorGlbPath]);

  // 選び直しの過程も分析できるよう、着色を変えるたびに記録する
  const changePart = (part) => {
    setLocalSelectedPart(part);
    logPartColorChange(part);
  };

  // ModelViewer への refs（カメラ位置の問い合わせと、ヒントで視点を動かすため）
  const leftViewerRef = React.useRef(null);
  const rightViewerRef = React.useRef(null);
  // ヒント：モチーフ・誤りの両方の3Dモデルを「おかしさが分かる視点」へイージング移動させる（X-2 と同じ動き）。
  // 着色を変えてもシーンは作り直されない（モデルだけ差し替わる）ので、ヒントの視点はそのまま残る
  const { animating: hintAnimating, shown: hintShown, showHint, resetView, restingPosition } = useHintCamera([leftViewerRef, rightViewerRef], true, errorGlbPath);

  const getCameraFromRefs = () => {
    // ヒントの視点を表示中なら、ヒントを押す前の視点を X-2 へ引き継ぐ（useHintCamera の restingPosition を参照）
    const rest = restingPosition(1) || restingPosition(0);
    if (rest) return rest;
    try {
      if (rightViewerRef.current && typeof rightViewerRef.current.getCameraPosition === 'function') {
        const c = rightViewerRef.current.getCameraPosition();
        if (c) return c;
      }
      if (leftViewerRef.current && typeof leftViewerRef.current.getCameraPosition === 'function') {
        const c = leftViewerRef.current.getCameraPosition();
        if (c) return c;
      }
    } catch (e) {
      console.warn('Failed to get camera position from viewer refs', e);
    }
    return null;
  };

  const finalizeSelection = (selfExpText, detail) => {
    // カメラ情報を収集して onComplete を呼ぶ（retry を含む）
    let attempts = 0;
    const trySend = () => {
      attempts++;
      const cameraToSend = getCameraFromRefs();

      if (!cameraToSend && attempts < 4) {
        setTimeout(trySend, 120);
        return;
      }

      // 親コールバックに選択部位とカメラ位置を渡す
      if (typeof parentSetSelectedPart === "function") parentSetSelectedPart(selectedPart);
      if (typeof onComplete === "function") onComplete(selectedPart, cameraToSend, selfExpText, detail);
    };

    trySend();
  };

  // 【次に進む】：X-2 へ。確定した部品は PartNextButton が step_responses に記録する
  const handleUnderstood = (selfExpText, detail) => {
    finalizeSelection(selfExpText, detail);
  };

  // 各カラム＝上段の画像（モチーフは写真、誤りはデッサン）＋下段の3Dモデル。
  // 見る対象（実物とデッサン）と3Dモデルを縦に対応させる並びは GazeControl のカラムと同じ
  const motifRef = getMotifReference(motifGlbPath);
  const viewers = [
    {
      title: "モチーフ", glbPath: motifGlbPath, caption: "左", canvasId: "leftModelCanvas", ref: leftViewerRef,
      imageLabel: motifRef.label, imageSrc: motifRef.src, imageAlt: "モチーフ",
    },
    {
      title: "誤り", glbPath: errorGlbPath, caption: "右", canvasId: "rightModelCanvas", ref: rightViewerRef,
      imageLabel: "デッサン", imageSrc: getDrawingUrl(errorGlbPath, "error"), imageAlt: "誤りのあるデッサン",
    },
  ];

  // 外側の div：演習ルートの .content > div（縦積み）を受ける枠。ShapeChange も同じく div で包まれている
  return (
    <div>
      <ExerciseWorkspace
        eyebrow="おかしい箇所を選ぶ"
        number={number}
        instruction={locked
          ? 'この問題は回答済みです。選んだ部品（赤く表示）を見直せます。'
          : (
            <>
              <p className="exercise-stage__lead">
                モチーフと見比べて、デッサンのおかしい箇所を選ぼう（分からなければ「△わからない」を選択してください）
              </p>
              <StepGuide current={!selectedPart ? 1 : 2} />
            </>
          )}
      >
        <div className="partselect-columns">
          {viewers.map((v) => (
            <div key={v.canvasId} className="partselect-column">
              <h2 className="heading-lg mb-1">{v.title}</h2>
              <h3 className="text-subtitle mb-1">{v.imageLabel}</h3>
              <CachedImage className="partselect-column__image" src={v.imageSrc} alt={v.imageAlt} />
              <h3 className="text-subtitle mt-2 mb-1">3Dモデル</h3>
              <ModelViewer
                glbPath={v.glbPath}
                caption={v.caption}
                canvasId={v.canvasId}
                addColor={selectedPart}
                showToolbar={SHOW_MODEL_TOOLBAR}
                ref={v.ref}
              />
            </div>
          ))}
        </div>

        <div className="row-center mt-4 gap-4">
          {/* 手順①。まだ選んでいないときはプルダウン自体を点滅させて「最初はここ」と示す */}
          <ColoredPartSelecter
            selectedPart={selectedPart}
            setSelectedPart={changePart}
            isCube={motifGlbPath === "cube"}
            disabled={locked}
            label={<><span className="step-badge">1</span>おかしい箇所を選ぶ</>}
            attention={!selectedPart}
            allowUnknown
          />
          <ShowHintButton glbPath={errorGlbPath} onShow={showHint} onReset={resetView} shown={hintShown} disabled={hintAnimating} />
          {/* 手順② */}
          <PartNextButton selectedPart={selectedPart} locked={locked} onAdvance={handleUnderstood} />
        </div>
        <DirectionNote glbPath={errorGlbPath} />
      </ExerciseWorkspace>

      <div className="row-center">
        <ToHomeButton buttonText="Home画面に戻る" />
      </div>
    </div>
  );
}
